/* PACKAGES */
import React from 'react';

/* UTILS */
import { fillArray } from '../../utils/array';
import { getIndex } from '../../utils/slider';

/* STYLES */
import s from './Slider.module.scss';

// Perspective-style scale/opacity falloff the further a dot sits from the active one,
// so a long nav reads as receding into the distance rather than a flat row (Instagram-style dots).
const DOT_SCALE_STEPS = [1, 0.82, 0.64, 0.46, 0.3];
const DOT_OPACITY_STEPS = [1, 0.8, 0.55, 0.35, 0.2];
const getStep = (steps, distance) => steps[Math.min(distance, steps.length - 1)];

// Fixed footprint of each dot's slot (see `_nav_button` in Slider.module.scss). Kept as a
// constant here so the track's translate offset lines up exactly with the CSS layout.
const SLOT_WIDTH = 32;

const SliderNav = ({ infinite, totalUniqueSlides, widthSubColumn, totalSubColumns, index, goToSlide, maxVisible = 5 }) => {
	const navIndex = getIndex(infinite, totalUniqueSlides, false, widthSubColumn, totalSubColumns, index);
	const activeIndex = navIndex - infinite;

	// Only a window of slots is visible at once, so the nav doesn't grow unbounded on sliders
	// with lots of slides. Every dot is rendered in one continuous track that slides inside a
	// clipped viewport, so paginating physically carries the dots sideways instead of just
	// swapping sizes in place.
	const windowSize = Math.max(1, Math.min(maxVisible, totalUniqueSlides));
	const halfWindow = Math.floor((windowSize - 1) / 2);
	const windowStart = Math.min(
		Math.max(activeIndex - halfWindow, 0),
		totalUniqueSlides - windowSize,
	);

	const handleNavClick = (e) => {
		e.preventDefault();
		const toIndex = e.currentTarget.value;
		goToSlide((parseInt(toIndex, 10) + infinite), e);
	};

	return (
		<div className={s.slider_nav}>
			<div className={s.slider_nav_buttons} style={{ width: `${SLOT_WIDTH * windowSize}px` }}>
				<div className={s.slider_nav_track} style={{ transform: `translateX(${-1 * windowStart * SLOT_WIDTH}px)` }}>
					{
						fillArray(totalUniqueSlides, (_, i) => {
							const isActive = i === activeIndex;
							const isVisible = i >= windowStart && i < windowStart + windowSize;
							const distance = Math.abs(i - activeIndex);
							const dotStyle = {
								'--nav-scale': getStep(DOT_SCALE_STEPS, distance),
								'--nav-opacity': getStep(DOT_OPACITY_STEPS, distance),
							};

							return (
								<button type="button" className={[s.slider_nav_button, (isActive ? s.slider_nav_button__active : null)].join(' ')} key={`pagination-${i}`} value={i} onClick={handleNavClick} style={dotStyle} tabIndex={isVisible ? 0 : -1} aria-hidden={isVisible ? undefined : 'true'} aria-label={`Go to slide ${i + 1} of ${totalUniqueSlides}`} aria-current={isActive ? 'true' : undefined} />
							);
						})
					}
				</div>
			</div>
		</div>
	);
};

export default SliderNav;
